import React from 'react';
import { Chip } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import PurchaseAppService from '../../../../application/platform/scp/purchase/purchase-app-service';
import { usePagedList } from '../../../../hooks/use-paged-list';
import { dataGridDefaults } from '../../../../components/list-grid-panel';
import { EditAction, DeleteAction, OpenLinkAction } from '../../../../components/row-actions';
import { TwoLineCell } from '../../../../components/two-line-cell';
import { IPurchasePagedResponse } from '../../../../model/scp/purchase/response/purchase-paged-response';
import { IPurchasePageRequest } from '../../../../model/scp/purchase/request/purchase-paged-request';

type ChipColor = 'primary' | 'secondary' | 'warning' | 'error' | 'success' | 'default';

const DISPUTE_MODE_COLOR: Record<number, ChipColor> = { 1: 'primary', 2: 'secondary', 3: 'warning', 4: 'error', 5: 'success', 6: 'default' };

export interface PurchaseDataTableProps {
    parameters: IPurchasePageRequest;
    searchTrigger: number;
    onPaginationChange: (page: number, pageSize: number) => void;
    onEdit: (row: IPurchasePagedResponse) => void;
    onDelete: (row: IPurchasePagedResponse, onDeleted: () => void) => void;
}

export default function PurchaseDataTable({ parameters, searchTrigger, onPaginationChange, onEdit, onDelete }: PurchaseDataTableProps) {
    const { rows, rowCount, loading, reload } = usePagedList(PurchaseAppService.listPurchase, parameters, searchTrigger, 'Não foi possível carregar as compras e licitações');

    const columns: GridColDef<IPurchasePagedResponse>[] = [
        { field: 'id', headerName: 'Id', width: 90, sortable: false },
        {
            field: 'number', headerName: 'Nº Compra', minWidth: 160, flex: 0.7, sortable: false,
            renderCell: (params) => <TwoLineCell primary={params.value} secondary={params.row.numberProcess ? `Processo ${params.row.numberProcess}` : undefined} bold />
        },
        { field: 'year', headerName: 'Exercício', width: 100, sortable: false },
        {
            field: 'title', headerName: 'Título', flex: 1.5, minWidth: 220, sortable: false,
            renderCell: (params) => <TwoLineCell primary={params.value} secondary={params.row.object} />
        },
        { field: 'modality', headerName: 'Modalidade', flex: 1, minWidth: 150, sortable: false },
        {
            field: 'disputeMode', headerName: 'Modo de disputa', flex: 1, minWidth: 150, sortable: false,
            renderCell: (params) => params.value ? <Chip size="small" label={params.value} color={DISPUTE_MODE_COLOR[params.row.disputeModeId] || 'primary'} /> : null
        },
        {
            field: 'actions', headerName: 'Ações', width: 130, sortable: false, filterable: false,
            renderCell: (params) => (
                <React.Fragment>
                    {params.row.url && <OpenLinkAction url={params.row.url} title="Acessar no portal" />}
                    <EditAction onClick={() => onEdit(params.row)} />
                    <DeleteAction onClick={() => onDelete(params.row, reload)} />
                </React.Fragment>
            )
        }
    ];

    return (
        <DataGrid
            {...dataGridDefaults}
            rows={rows}
            columns={columns}
            loading={loading}
            rowCount={rowCount}
            rowHeight={60}
            paginationMode="server"
            paginationModel={{ page: parameters.page ?? 0, pageSize: parameters.pageSize ?? 100 }}
            onPaginationModelChange={(model) => onPaginationChange(model.page, model.pageSize)}
        />
    );
}
